<template>
    <div>
    <div v-if="!appLoading" class="h-100">
        <b-container
            class="customer-results__page-container"
        >
            <div v-if="error">
                <img
                    v-svg-inline
                    src="@/assets/ui/bq-logo-os-green.svg"
                />
                <b-alert class="mt-3"  variant="danger" show>An error occured.</b-alert>
            </div>
            <div v-else-if="results">
                <result-page
                    class="customer-results__result-page"
                    v-if="results"
                    :result="results && results[currentPage-1]"
                    :progress="progressText"
                >
                    <template v-slot:content>
                        <b-row class="result-page__container mt-4 mb-4">
                            <b-col class="d-flex justify-content-center">
                                <div :class="showAvatarChart ? 'result-page__avatar-chart-container mb-4' : 'result-page__icon'">
                                    <component
                                        :is="currentVisComponent"
                                        v-bind="currentVisProps"
                                    />
                                </div>
                            </b-col>
                        </b-row>
                        <b-row class="result-page__content-h text-center mb-5">
                            <b-col>
                                <span v-html="results[currentPage-1].content.title" />
                            </b-col>
                        </b-row>
                        <b-row class="result-page__content">
                            <b-col>
                                <span v-html="results[currentPage-1].content.text" />
                            </b-col>
                        </b-row>
                    </template>
                </result-page>

                <div class="customer-results__nav-container w-100">
                    <indexed-nav
                        class="customer-results__nav"
                        :pages="results.length"
                        :currentPage="currentPage"
                        @next="handleNext"
                        @prev="handlePrev"
                        @click="handlePageClick"
                    />
                </div>
            </div>
        </b-container>
    </div>
    <div v-else>
        <b-spinner variant="bq-green"></b-spinner>
    </div>
    </div>

</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import { TEXT_TYPES } from '@/constants/i18n'
import { ASSETS_URL, COLOR_VARIANTS } from '@/constants/misc'

import bqColorPage from '@/components/ui/bqColorPage'
import resultPage from '@/components/results/resultPage'
import indexedNav from '@/components/ui/indexedNav'
import controlButton from '@/components/ui/controlButton'
import { SimpleSVG } from 'vue-simple-svg'
import avatarChart from '@/components/ui/avatarChart'
import { waitForAppReady } from '@/utils/waitForAppReady';


export default {
    name: 'CustomerResultsContainer',
    components: {
        'bq-color-page': bqColorPage,
        'result-page': resultPage,
        'indexed-nav': indexedNav,
        'avatar-chart': avatarChart,
        'control-button': controlButton
    },
    props: {
        interactionInstanceToken: {
            type: String
        }
    },
    data() {
        return {
            ASSETS_URL,
            currentPage: 1,
            results: null,
            error: false,
        }
    },
    computed: {
        ...mapGetters([
            'interactionName',
            'resultsUser',
            'appLoading',
            'locale',
        ]),
        progressText() {
            return `${this.currentPage} ${this.$t('INTERACTION_BASE_OF')} ${this.results.length}`
        },
        currentResult() {
            return this.results.find(result => result.number === this.currentPage)
        },
        showAvatarChart() {
            return this.currentResult.key === "BQA_DIM_FINRISK"
        },
        currentVisComponent() {
            if(this.showAvatarChart) {
                return avatarChart
            }

            return SimpleSVG
        },
        currentVisProps() {
            if(this.showAvatarChart) {
                return {
                    accentuatedAvatarIndex: this.currentResult.content.result,
                    labelMin: this.getSimpleText("AVATAR_CHART_SCALE_LABEL_MIN"),
                    labelMax: this.getSimpleText("AVATAR_CHART_SCALE_LABEL_MAX"),
                    labelAvatar: this.getSimpleText("AVATAR_CHART_AVATAR_LABEL"),
                    highlightColor: '#00A883',
                }
            }

            return {
                src: `${ASSETS_URL}${this.currentResult.content.icon}`,
                width: '100%',
                height: '100%'
            }
        },
    },
    methods: {
        ...mapActions([
            'fetchBQAParticipantResults',
            'fetchInteractionInstanceDimensions',
            'fetchTranslations',
            'setLoadingUntilResolved',
            'setColorVariant'
        ]),
        handleNext() {
            this.currentPage = this.currentPage + 1
        },
        handlePrev() {
            this.currentPage = this.currentPage - 1
        },
        handlePageClick(pageNumber) {
            this.currentPage = pageNumber
        },
        getSimpleText(key) {
            return this.simpleTexts.find(text => text.key === key).text
        }
    },
    async mounted() {
        await waitForAppReady(this.$store);
        this.setColorVariant(COLOR_VARIANTS.BQ_GREEN)
        try {
            const [_, participantResults] = await this.setLoadingUntilResolved([
                this.fetchTranslations({ textType: TEXT_TYPES.INTERACTION_BASE }), 
                this.fetchBQAParticipantResults({
                    interactionInstanceToken: this.interactionInstanceToken, locale: this.locale.id 
                })
            ])
            document.title = this.$t('INTERACTION_BASE_CUSTOMER_RESULTS_APPLICATION_TITLE_TEXT')
            this.results = participantResults.results
            this.simpleTexts = participantResults.simpleTexts        
        } catch(e) {
            this.error = true
        }
    }
}
</script>
<style lang="scss">
.customer-results__intro-content {
    margin-top: 250px;
}

.customer-results__page-container {
    @media screen and (max-width: 767px) {
        padding-bottom: 100px;
    }
}

.customer-results__result-page {
    @media screen and (min-width: 992px) {
        padding: 0 200px;
    }
}

.result-page__avatar-chart-container {
    width: 100%;
    @media screen and (min-width: 992px) {
        width: 70%;
    }
}

.customer-results__nav-container {
    @media screen and (min-width: 992px) {
        padding: 0 120px;
    }
}

.customer-results__nav {
    position: fixed;
    bottom: 0;
    left: 0;
    @media screen and (min-width: 768px) {
        margin-top: 100px;
        position: relative;
    }
}
</style>
